import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    // Fields
    "serviceField", "providerField", "dateField", "timeField", "bookingTypeField",
    // Sections
    "section", "dateTimeSection", "timeSlots", "dateScroller", "scrollContainer",
    // Grids & Edit Buttons
    "serviceGrid", "editServiceBtn", "providerGrid", "editProviderBtn",
    // Summary Targets (These often cause errors if missing)
    "summaryService", "summaryDuration", "summaryPrice", "summaryProvider", "summaryDate", "summaryTime",
    // Buttons
    "submitButton", "mobileSubmit", "mobilePrice"
  ];

  static values = {
    baseUrl: String,
    currency: String,
    locale: String,
    flexibleDate: String,
    contactOptions: String,
    durationOnRequest: String,
    priceOnRequest: String,
    noDates: String,
    checking: String,
    loadError: String,
    noSlots: String,
    providerChoice: Boolean
  };

  connect() {
    this.checkSingleProvider();

    // Resume state if browser back button used or URL params pre-filled
    if (this.serviceFieldTarget.value) {
      this.unlockSection(1);
      // Visually select the service card if pre-filled
      const serviceCards = this.element.querySelectorAll('[data-group="service"]');
      serviceCards.forEach(card => {
        const radio = card.querySelector('input[type="radio"]');
        if (radio && radio.value === this.serviceFieldTarget.value) {
          radio.checked = true;
          this.updateRadioVisuals("service", card);
          this.updateSummaryData(card.dataset);
        }
      });
      this.filterProvidersForService(this.serviceFieldTarget.value);
    }

    if (this.providerFieldTarget.value) {
      this.unlockSection(2);
      // Visually select the provider card if pre-filled
      const providerCards = this.element.querySelectorAll('[data-group="provider"]');
      providerCards.forEach(card => {
        const radio = card.querySelector('input[type="radio"]');
        if (radio && radio.value === this.providerFieldTarget.value) {
          radio.checked = true;
          this.updateRadioVisuals("provider", card);
          if (this.hasSummaryProviderTarget) {
            this.summaryProviderTarget.textContent = card.dataset.name;
          }
          this.fetchAvailableDates(radio.value);
        }
      });
    }

    // If both service and provider are already selected, scroll smoothly
    if (this.serviceFieldTarget.value && this.providerFieldTarget.value) {
      this.unlockSection(2);
      
      // Visually collapse the service and provider sections to jump straight to booking type/date
      if (this.hasServiceGridTarget) this.serviceGridTarget.classList.add("hidden");
      if (this.hasEditServiceBtnTarget) this.editServiceBtnTarget.classList.remove("hidden");
      
      if (this.hasProviderGridTarget) this.providerGridTarget.classList.add("hidden");
      if (this.hasEditProviderBtnTarget) this.editProviderBtnTarget.classList.remove("hidden");

      setTimeout(() => {
        // Start scrolling directly from the booking type step (index 2)
        this.unlockAndScrollTo(2);
      }, 150);
      
      // If booking type is already selected, trigger its logic
      const selectedBookingType = this.element.querySelector('input[name="booking_type_ui"]:checked');
      if (selectedBookingType) {
        this.bookingTypeChanged({ target: selectedBookingType });
      }
    }
  }

  editService() {
    if (this.hasServiceGridTarget) this.serviceGridTarget.classList.remove("hidden");
    if (this.hasEditServiceBtnTarget) this.editServiceBtnTarget.classList.add("hidden");
    this.unlockAndScrollTo(0);
  }

  editProvider() {
    if (this.hasProviderGridTarget) this.providerGridTarget.classList.remove("hidden");
    if (this.hasEditProviderBtnTarget) this.editProviderBtnTarget.classList.add("hidden");
    this.unlockAndScrollTo(1);
  }

  // --- ACTIONS ---

  selectService(event) {
    this.hapticFeedback();
    const card = event.currentTarget;
    const radio = card.querySelector('input');
    radio.checked = true;

    this.updateRadioVisuals("service", card);
    this.serviceFieldTarget.value = radio.value;

    // SAFE UPDATE: Won't crash if summary is missing
    this.updateSummaryData(card.dataset);

    this.filterProvidersForService(radio.value);

    if (this.providerFieldTarget.value) {
      this.unlockAndScrollTo(2); // Go to Type/Date
    } else {
      this.unlockAndScrollTo(1); // Go to Provider
    }
  }

  selectProvider(event) {
    this.hapticFeedback();
    const card = event.currentTarget;
    const radio = card.querySelector('input');
    radio.checked = true;

    this.updateRadioVisuals("provider", card);
    this.providerFieldTarget.value = radio.value;
    
    // SAFE UPDATE
    if (this.hasSummaryProviderTarget) {
      this.summaryProviderTarget.textContent = card.dataset.name;
    }

    this.fetchAvailableDates(radio.value);
    this.unlockAndScrollTo(2);
  }

  bookingTypeChanged(event) {
    this.hapticFeedback();
    const type = event.target.value;
    this.bookingTypeFieldTarget.value = type;

    if (type === "flexible_date") {
      if (this.hasDateTimeSectionTarget) {
        this.dateTimeSectionTarget.classList.add("hidden");
      }
      if (this.hasSummaryDateTarget) this.summaryDateTarget.textContent = this.flexibleDateValue;
      if (this.hasSummaryTimeTarget) this.summaryTimeTarget.textContent = this.contactOptionsValue;
      this.unlockAndScrollTo(4);
      this.enableSubmit();
    } else if (type === "specific_date") {
      if (this.hasDateTimeSectionTarget) {
        this.dateTimeSectionTarget.classList.remove("hidden");
      }
      // Only fetch dates if provider is selected
      if (this.providerFieldTarget.value) {
        this.fetchAvailableDates(this.providerFieldTarget.value);
      }
      this.unlockAndScrollTo(3);
    }
  }

  async selectDate(event) {
    this.hapticFeedback();
    const btn = event.currentTarget;
    
    // UI Update
    if (this.hasDateScrollerTarget) {
      this.dateScrollerTarget.querySelectorAll("button").forEach(b => {
        b.classList.remove("ring-2", "ring-indigo-600", "bg-indigo-50", "dark:bg-indigo-900/30");
        b.classList.add("border-slate-200", "dark:border-slate-700");
      });
      btn.classList.remove("border-slate-200", "dark:border-slate-700");
      btn.classList.add("ring-2", "ring-indigo-600", "bg-indigo-50", "dark:bg-indigo-900/30");
    }

    this.dateFieldTarget.value = btn.dataset.date;
    
    // SAFE UPDATE: Format Date for Summary
    if (this.hasSummaryDateTarget) {
       const dateObj = new Date(btn.dataset.date + "T00:00:00");
       this.summaryDateTarget.textContent = dateObj.toLocaleDateString(this.localeValue, { weekday: 'short', day: 'numeric', month: 'short' });
    }

    this.loadTimeSlots(this.providerFieldTarget.value, btn.dataset.date);
  }

  selectTime(event) {
    this.hapticFeedback();
    const btn = event.currentTarget;
    this.timeFieldTarget.value = btn.dataset.time;

    // SAFE UPDATE
    if (this.hasSummaryTimeTarget) {
      this.summaryTimeTarget.textContent = btn.innerText;
    }

    // Visuals
    if (this.hasTimeSlotsTarget) {
      this.timeSlotsTarget.querySelectorAll('button').forEach(b => {
        b.className = "px-4 py-3 rounded-xl border border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200 transition-all";
      });
      btn.className = "px-4 py-3 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-bold shadow-md transform scale-105 transition-all";
    }

    this.unlockAndScrollTo(4); // Go to Details
    this.checkFormReadiness();
  }

  // --- HELPERS ---

  updateSummaryData(data) {
    // 1. Service Name
    if(this.hasSummaryServiceTarget) {
      this.summaryServiceTarget.textContent = data.name;
    }
    
    // 2. Duration
    if(this.hasSummaryDurationTarget) {
       const duration = data.duration;
       const showDuration = data.showDuration;
       if (showDuration === "1" && duration) {
          this.summaryDurationTarget.textContent = `${duration} min`;
       } else {
          this.summaryDurationTarget.textContent = this.durationOnRequestValue;
       }
    }

    // 3. Price
    const price = parseFloat(data.price || 0);
    const showPrice = data.showPrice === "1";
    let priceText = this.priceOnRequestValue;
    
    if (showPrice && price > 0) {
      const currencyCode = this.localeValue === "pt-BR" ? "BRL" : "USD";
      priceText = new Intl.NumberFormat(this.localeValue, { style: "currency", currency: currencyCode }).format(price);
    }

    if(this.hasSummaryPriceTarget) this.summaryPriceTarget.textContent = priceText;
    
    // Mobile Price Animation
    if(this.hasMobilePriceTarget) {
        this.mobilePriceTarget.style.opacity = '0';
        setTimeout(() => {
            this.mobilePriceTarget.textContent = priceText;
            this.mobilePriceTarget.style.opacity = '1';
        }, 150);
    }
  }

  hapticFeedback() {
    if (window.navigator && window.navigator.vibrate) {
      window.navigator.vibrate(10);
    }
  }

  unlockAndScrollTo(index) {
    if (!this.hasSectionTarget) return;
    
    const section = this.sectionTargets[index];
    if (section) {
      section.classList.remove("opacity-40", "grayscale", "pointer-events-none");
      setTimeout(() => {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }

  unlockSection(index) {
     if (!this.hasSectionTarget) return;
     const section = this.sectionTargets[index];
     if(section) section.classList.remove("opacity-40", "grayscale", "pointer-events-none");
  }

  updateRadioVisuals(groupName, selectedCard) {
    const allCards = this.element.querySelectorAll(`[data-group="${groupName}"]`);
    allCards.forEach(c => {
      c.classList.remove("border-indigo-600", "ring-2", "ring-indigo-500/20", "bg-indigo-50/50", "dark:bg-indigo-900/10");
      c.classList.add("border-transparent", "hover:border-slate-200");
      const check = c.querySelector(".check-icon");
      if(check) check.classList.remove("opacity-100", "scale-100");
    });

    selectedCard.classList.remove("border-transparent", "hover:border-slate-200");
    selectedCard.classList.add("border-indigo-600", "ring-2", "ring-indigo-500/20", "bg-indigo-50/50", "dark:bg-indigo-900/10");
    const check = selectedCard.querySelector(".check-icon");
    if(check) check.classList.add("opacity-100", "scale-100");
  }

  enableSubmit() {
    if(this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false;
    if(this.hasMobileSubmitTarget) this.mobileSubmitTarget.disabled = false;
  }

  checkFormReadiness() {
    if (this.serviceFieldTarget.value && 
        this.providerFieldTarget.value && 
        (this.bookingTypeFieldTarget.value === 'flexible_date' || (this.dateFieldTarget.value && this.timeFieldTarget.value))) {
      this.enableSubmit();
    }
  }

  checkSingleProvider() {
    // Check if section target exists before accessing index 1
    if (this.sectionTargets.length > 1) {
      const providerSection = this.sectionTargets[1];
      if (providerSection && providerSection.dataset.providerCount === "1") {
        const card = providerSection.querySelector('[data-group="provider"]');
        if (card) {
          const radio = card.querySelector('input');
          radio.checked = true;
          this.providerFieldTarget.value = radio.value;
          this.updateRadioVisuals("provider", card);
          if (this.hasSummaryProviderTarget) {
            this.summaryProviderTarget.textContent = card.dataset.name;
          }
          this.fetchAvailableDates(radio.value);
        }
      }
    }
  }

  filterProvidersForService(serviceId) {
    const cards = Array.from(this.element.querySelectorAll('[data-group="provider"]'));
    const availableCards = cards.filter(card => {
      const serviceIds = (card.dataset.serviceIds || "").split(",").filter(Boolean);
      const available = serviceIds.includes(serviceId);
      card.classList.toggle("hidden", !available);
      return available;
    });

    const selectedCard = cards.find(card => {
      const radio = card.querySelector('input[type="radio"]');
      return radio && radio.value === this.providerFieldTarget.value;
    });

    if (!selectedCard || selectedCard.classList.contains("hidden")) {
      this.providerFieldTarget.value = "";
      if (this.hasSummaryProviderTarget) this.summaryProviderTarget.textContent = "--";
    }

    if (!this.providerFieldTarget.value && availableCards.length && (!this.providerChoiceValue || availableCards.length === 1)) {
      const providerCard = availableCards[0];
      const radio = providerCard.querySelector('input[type="radio"]');
      if (radio) {
        radio.checked = true;
        this.providerFieldTarget.value = radio.value;
        this.updateRadioVisuals("provider", providerCard);
        if (this.hasSummaryProviderTarget) this.summaryProviderTarget.textContent = providerCard.dataset.name;
        this.fetchAvailableDates(radio.value);
      }
    }
  }

  async fetchAvailableDates(providerId) {
    if (!this.hasDateScrollerTarget) return;

    // UI Feedback
    const buttons = this.dateScrollerTarget.querySelectorAll("button");
    buttons.forEach(btn => {
      btn.disabled = true;
      btn.classList.add("opacity-50", "cursor-not-allowed");
    });

    let anyAvailable = false;
    try {
      const response = await fetch(`${this.baseUrlValue}/providers/${providerId}/available_dates`);
      if (!response.ok) throw new Error("Network response was not ok");
      const availableDates = await response.json();

      buttons.forEach(btn => {
        const dateStr = btn.dataset.date;
        const dateData = availableDates.find(d => d.date === dateStr);
        if (dateData && dateData.available) {
          btn.disabled = false;
          btn.classList.remove("opacity-50", "cursor-not-allowed");
          anyAvailable = true;
        }
      });
    } catch (error) {
      console.error("Error fetching dates:", error);
    }

    // Show a message if no dates are available
    const prompt = this.element.querySelector('[data-appointment-target="datePrompt"]');
    if (prompt) {
      if (!anyAvailable) {
        prompt.textContent = this.noDatesValue;
        prompt.classList.remove("hidden");
      } else {
        prompt.classList.add("hidden");
      }
    }
  }

  async loadTimeSlots(providerId, date) {
    if (!this.hasTimeSlotsTarget) return;

    try {
      this.timeSlotsTarget.innerHTML = `<div class="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center text-slate-500 text-xs animate-pulse">${this.escapeHtml(this.checkingValue)}</div>`;
      const resp = await fetch(`${this.baseUrlValue}/providers/${providerId}/slots?date=${date}`);
      if (!resp.ok) throw new Error("Slots error");
      const slots = await resp.json();
      this.renderSlots(slots);
    } catch (e) {
      console.error(e);
      this.timeSlotsTarget.innerHTML = `<span class="text-red-500 text-xs">${this.escapeHtml(this.loadErrorValue)}</span>`;
    }
  }

  renderSlots(slots) {
    if (!this.hasTimeSlotsTarget) return;

    if (!slots.length) {
      this.timeSlotsTarget.innerHTML = `<div class="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center text-slate-500 text-sm">${this.escapeHtml(this.noSlotsValue)}</div>`;
      return;
    }
    const html = slots.map(slot => `
      <button type="button" 
        data-action="click->appointment#selectTime" 
        data-time="${slot.time}"
        class="px-4 py-3 rounded-xl border border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700 hover:border-indigo-500 text-sm font-medium text-slate-700 dark:text-slate-200 transition-all active:scale-95">
        ${slot.formatted}
      </button>
    `).join("");
    this.timeSlotsTarget.innerHTML = `<div class="grid grid-cols-3 sm:grid-cols-4 gap-3">${html}</div>`;
  }

  escapeHtml(value) {
    const element = document.createElement("div");
    element.textContent = value || "";
    return element.innerHTML;
  }
}
